import { motion } from 'framer-motion';
import { useTranslation } from 'react-i18next';

// ─── Floating sector icons ────────────────────────────────────────────
const SECTORS = [
    { icon: '🏥', bg: 'bg-rose-500', pos: '-top-8 -left-6 lg:-left-12' },
    { icon: '📚', bg: 'bg-sky-500', pos: 'top-16 -right-6 lg:-right-16' },
    { icon: '🏭', bg: 'bg-amber-500', pos: 'bottom-20 -left-8 lg:-left-16' },
    { icon: '📊', bg: 'bg-emerald-500', pos: '-bottom-6 -right-4 lg:-right-10' },
];

function FloatingIcon({ icon, bg, pos, delay }) {
    return (
        <motion.div
            animate={{ y: [0, -10, 0] }}
            transition={{ duration: 3.5 + delay, repeat: Infinity, ease: 'easeInOut' }}
            className={`absolute z-20 ${pos}`}
        >
            <div className={`w-12 h-12 rounded-2xl ${bg} shadow-xl flex items-center justify-center text-2xl`}>
                {icon}
            </div>
        </motion.div>
    );
}

// ─── Tablet/Dashboard mockup ──────────────────────────────────────────────────────────
// ─── Desktop Monitor Mockup ──────────────────────────────────────────────────────────
function MonitorMockup() {
    const { t } = useTranslation();
    return (
        <motion.div
            initial={{ opacity: 0, scale: 0.9, y: 20 }}
            animate={{ opacity: 1, scale: 1, y: 0 }}
            transition={{ duration: 0.7, delay: 0.2 }}
            className="relative w-full mx-auto mt-8"
        >
            {/* Monitor Screen Frame */}
            <div className="relative bg-slate-900 rounded-2xl shadow-2xl border-[10px] border-slate-900 overflow-hidden flex flex-col ring-1 ring-white/10">

                {/* Screen Content */}
                <div className="bg-slate-50 p-6 h-[320px] lg:h-[380px] overflow-hidden flex flex-col w-full">
                    {/* Header */}
                    <div className="flex justify-between items-center mb-6">
                        <div className="flex items-center gap-3">
                            <div className="w-10 h-10 rounded-xl bg-brand flex items-center justify-center text-white text-sm font-bold shadow-md">CAU</div>
                            <div>
                                <span className="text-base font-bold text-slate-800 leading-tight block">{t('mockup_title')}</span>
                                <span className="text-[10px] uppercase font-semibold tracking-wider text-slate-400">{t('mockup_subtitle')}</span>
                            </div>
                        </div>
                        <div className="flex gap-2">
                            <div className="w-2 h-2 rounded-full bg-rose-500" />
                            <div className="w-2 h-2 rounded-full bg-amber-500" />
                            <div className="w-2 h-2 rounded-full bg-emerald-500" />
                        </div>
                    </div>

                    {/* Stats boxes */}
                    <div className="grid grid-cols-2 gap-4 mb-6">
                        <div className="bg-white p-5 rounded-2xl shadow-sm border border-slate-200 hover:shadow-md transition-shadow">
                            <div className="flex justify-between items-start mb-2">
                                <p className="text-[11px] text-slate-500 font-bold uppercase tracking-wider">{t('mockup_med')}</p>
                                <span className="text-xs font-black text-rose-500 bg-rose-50 px-2 py-0.5 rounded-md">84%</span>
                            </div>
                            <div className="w-full bg-slate-100 h-2 rounded-full overflow-hidden mt-4">
                                <div className="bg-gradient-to-r from-rose-400 to-rose-500 h-full rounded-full w-[84%]" />
                            </div>
                        </div>
                        <div className="bg-white p-5 rounded-2xl shadow-sm border border-slate-200 hover:shadow-md transition-shadow">
                            <div className="flex justify-between items-start mb-2">
                                <p className="text-[11px] text-slate-500 font-bold uppercase tracking-wider">{t('mockup_edu')}</p>
                                <span className="text-xs font-black text-sky-500 bg-sky-50 px-2 py-0.5 rounded-md">92%</span>
                            </div>
                            <div className="w-full bg-slate-100 h-2 rounded-full overflow-hidden mt-4">
                                <div className="bg-gradient-to-r from-sky-400 to-sky-500 h-full rounded-full w-[92%]" />
                            </div>
                        </div>
                    </div>

                    {/* Lower Chart Area */}
                    <div className="bg-white rounded-2xl p-5 shadow-sm border border-slate-200 flex-1 flex flex-col justify-center">
                        <p className="text-[11px] font-bold text-slate-800 mb-4 tracking-wide uppercase">{t('mockup_ind')}</p>
                        <div className="space-y-4">
                            {[
                                { name: t('mockup_ind_prod'), val: '+18%', w: '70%', c: 'bg-amber-500' },
                                { name: t('mockup_ind_exp'), val: '+24%', w: '85%', c: 'bg-brand' }
                            ].map((item) => (
                                <div key={item.name} className="flex flex-col gap-1.5">
                                    <div className="flex justify-between items-center px-1">
                                        <span className="text-[11px] font-semibold text-slate-600">{item.name}</span>
                                        <span className="text-[11px] font-black text-emerald-500">{item.val}</span>
                                    </div>
                                    <div className="w-full bg-slate-100 h-2 rounded-full overflow-hidden">
                                        <div className={`h-full rounded-full ${item.c}`} style={{ width: item.w }} />
                                    </div>
                                </div>
                            ))}
                        </div>
                    </div>
                </div>

                {/* Bottom Bezel Highlights */}
                <div className="h-6 bg-slate-900 border-t border-slate-800 flex items-center justify-center relative">
                    {/* Logo/Brand mark on monitor bottom */}
                    <span className="text-[8px] tracking-[0.2em] text-slate-600 font-bold">CAU PRO</span>
                    {/* Power indicator light */}
                    <div className="absolute right-4 w-1.5 h-1.5 rounded-full bg-white/40 shadow-[0_0_8px_rgba(255,255,255,0.8)]" />
                </div>
            </div>

            {/* Monitor Stand */}
            <div className="flex flex-col items-center">
                {/* Neck */}
                <div className="w-20 h-10 bg-gradient-to-b from-slate-800 to-slate-700 shadow-inner relative z-0">
                    <div className="absolute inset-0 bg-black/20" />
                </div>
                {/* Base */}
                <div className="w-48 h-3.5 bg-gradient-to-b from-slate-600 to-slate-800 rounded-t-xl shadow-xl relative z-10 border-t border-slate-500/30" />
                <div className="w-56 h-1.5 bg-black/40 rounded-full blur-md mt-1" />
            </div>
        </motion.div>
    );
}

// ─── AuthLayout ──────────────────────────────────────────────────────────────
// Barcha ranglar CSS token orqali boshqariladi, dark: klaslari yo'q.
export default function AuthLayout({ children }) {
    const { t } = useTranslation();

    return (
        <div className="min-h-screen flex flex-col lg:flex-row bg-background">

            {/* ── Chap panel (vizual) ── */}
            <div
                className="relative lg:w-1/2 flex flex-col items-center justify-center py-16 px-8 overflow-hidden"
                style={{
                    background: `linear-gradient(135deg,
            rgb(var(--gradient-panel-from)),
            rgb(var(--gradient-panel-via)),
            rgb(var(--gradient-panel-to)))`,
                }}
            >
                {/* Pastki to'lqin bezak */}
                <svg
                    className="absolute bottom-0 left-0 right-0 opacity-20"
                    viewBox="0 0 1440 120"
                    preserveAspectRatio="none"
                >
                    <path
                        d="M0,64 C360,120 1080,0 1440,64 L1440,120 L0,120 Z"
                        fill="rgb(var(--color-bg))"
                    />
                </svg>

                {/* Nuqtalar panjarasi */}
                <div className="absolute top-8 right-8 grid grid-cols-5 gap-1.5 opacity-30">
                    {Array.from({ length: 25 }).map((_, i) => (
                        <div key={i} className="w-1.5 h-1.5 rounded-full bg-brand" />
                    ))}
                </div>

                {/* Hero sarlavha */}
                <motion.h1
                    initial={{ opacity: 0, y: -16 }}
                    animate={{ opacity: 1, y: 0 }}
                    transition={{ duration: 0.6 }}
                    className="text-center text-2xl lg:text-3xl font-extrabold text-primary mb-10 max-w-xs leading-snug"
                >
                    {t('hero_title')}
                </motion.h1>

                {/* Telefon + avatarlar konteyneri */}
                <div className="relative w-full max-w-lg mt-8">
                    {/* Glow effect orqada */}
                    <div className="absolute inset-0 flex items-center justify-center -z-10">
                        <div className="w-64 h-64 rounded-full bg-brand/20 blur-3xl" />
                    </div>

                    {/* Floating sector icons */}
                    {SECTORS.map((sec, i) => (
                        <FloatingIcon key={i} {...sec} delay={i * 0.4} />
                    ))}

                    <MonitorMockup />
                </div>
            </div>

            {/* ── O'ng panel (forma) ── */}
            <div className="lg:w-1/2 flex items-center justify-center px-6 py-12 bg-background">
                <motion.div
                    initial={{ opacity: 0, x: 24 }}
                    animate={{ opacity: 1, x: 0 }}
                    transition={{ duration: 0.5 }}
                    className="w-full max-w-md"
                >
                    {children}
                </motion.div>
            </div>
        </div>
    );
}
